'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { SITE_NAME } from '@/lib/site-config';

const STEPS = [
  { id: 1, label: 'Account', desc: 'Create your account' },
  { id: 2, label: 'Brand Profile', desc: 'Tell us about your brand' },
  { id: 3, label: 'Verification', desc: 'Verify your business' },
];

const BUSINESS_CATEGORIES = [
  'Electronics', 'Fashion & Apparel', 'Beauty & Personal Care',
  'Home & Kitchen', 'Sports & Fitness', 'Books & Stationery',
  'Toys & Games', 'Automotive', 'Health & Wellness', 'Food & Beverages',
  'Jewellery & Accessories', 'Other',
];

const INDIAN_STATES = [
  'Andhra Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Delhi', 'Goa',
  'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya',
  'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim',
  'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand',
  'West Bengal',
];

// Validation helpers
const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const PHONE_REGEX = /^(\+91[\-\s]?)?[6-9]\d{9}$/;
const PINCODE_REGEX = /^[1-9][0-9]{5}$/;

interface FieldErrors {
  [key: string]: string;
}

export default function SellerOnboardingClient() {
  const { user, signIn } = useAuth();
  const router = useRouter();

  const [step, setStep] = useState(user ? 2 : 1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  // Step 1 — Account
  const [authMode, setAuthMode] = useState<'signup' | 'signin'>('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Step 2 — Brand Profile
  const [brandName, setBrandName] = useState('');
  const [website, setWebsite] = useState('');
  const [businessCategory, setBusinessCategory] = useState('Electronics');
  const [businessDescription, setBusinessDescription] = useState('');
  const [contactEmail, setContactEmail] = useState(user?.email || '');
  const [contactPhone, setContactPhone] = useState('');

  // Step 3 — KYC
  const [gstNumber, setGstNumber] = useState('');
  const [panNumber, setPanNumber] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [pincode, setPincode] = useState('');
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [ifscCode, setIfscCode] = useState('');
  const [accountHolderName, setAccountHolderName] = useState('');

  const clearFieldError = (field: string) => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  // ── Step 1 validation ──────────────────────────────────────────────────────
  const validateStep1 = (): boolean => {
    const errors: FieldErrors = {};
    if (authMode === 'signup') {
      if (!fullName.trim()) errors.fullName = 'Full name is required.';
      else if (fullName.trim().length < 2) errors.fullName = 'Name must be at least 2 characters.';
    }
    if (!email.trim()) errors.email = 'Business email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = 'Enter a valid email address.';
    if (!password) errors.password = 'Password is required.';
    else if (password.length < 8) errors.password = 'Password must be at least 8 characters.';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── Step 2 validation ──────────────────────────────────────────────────────
  const validateStep2 = (): boolean => {
    const errors: FieldErrors = {};
    if (!brandName.trim()) errors.brandName = 'Brand name is required.';
    else if (brandName.trim().length < 2) errors.brandName = 'Brand name must be at least 2 characters.';
    if (website && !/^https?:\/\/.+\..+/.test(website)) errors.website = 'Enter a valid URL (e.g. https://yourbrand.com).';
    if (contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) errors.contactEmail = 'Enter a valid contact email.';
    if (contactPhone && !PHONE_REGEX.test(contactPhone.replace(/\s/g, ''))) errors.contactPhone = 'Enter a valid Indian mobile number.';
    if (!businessCategory) errors.businessCategory = 'Please select a business category.';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── Step 3 validation ──────────────────────────────────────────────────────
  const validateStep3 = (): boolean => {
    const errors: FieldErrors = {};
    // GST — optional but if provided must be valid
    if (gstNumber && !GST_REGEX.test(gstNumber)) errors.gstNumber = 'Invalid GST number format (e.g. 22AAAAA0000A1Z5).';
    // PAN — optional but if provided must be valid
    if (panNumber && !PAN_REGEX.test(panNumber)) errors.panNumber = 'Invalid PAN format (e.g. ABCDE1234F).';
    // Address fields — required for KYC
    if (!address.trim()) errors.address = 'Street address is required.';
    if (!city.trim()) errors.city = 'City is required.';
    if (!state) errors.state = 'Please select a state.';
    if (!pincode) errors.pincode = 'Pincode is required.';
    else if (!PINCODE_REGEX.test(pincode)) errors.pincode = 'Enter a valid 6-digit pincode.';
    // Bank details — required
    if (!accountHolderName.trim()) errors.accountHolderName = 'Account holder name is required.';
    if (!bankName.trim()) errors.bankName = 'Bank name is required.';
    if (!accountNumber.trim()) errors.accountNumber = 'Account number is required.';
    else if (accountNumber.length < 9 || accountNumber.length > 18) errors.accountNumber = 'Account number must be 9–18 digits.';
    if (!ifscCode) errors.ifscCode = 'IFSC code is required.';
    else if (!IFSC_REGEX.test(ifscCode)) errors.ifscCode = 'Invalid IFSC code format (e.g. HDFC0001234).';
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleStep1 = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!validateStep1()) return;
    setSaving(true);
    try {
      if (authMode === 'signup') {
        const { createClient } = await import('@/lib/supabase/client');
        const supabase = createClient();
        const { data, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { full_name: fullName },
            emailRedirectTo: `${process.env.NEXT_PUBLIC_SITE_URL || window.location.origin}/seller-onboarding/register`,
          },
        });
        if (signUpError) throw signUpError;

        // Set role to seller immediately on signup if session exists
        if (data.session && data.user) {
          await supabase
            .from('user_profiles')
            .update({ role: 'seller' })
            .eq('id', data.user.id);
          setStep(2);
        } else {
          setError('__email_confirmation__');
        }
      } else {
        await signIn(email, password);
        setStep(2);
      }
    } catch (err: any) {
      setError(err.message || 'Authentication failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleStep2 = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!validateStep2()) return;
    setStep(3);
  };

  const handleStep3 = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!validateStep3()) return;
    setSaving(true);
    try {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();

      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) throw new Error('Not authenticated. Please sign in again.');

      // Upsert seller profile — requires unique constraint on user_id (migration 20260826110000)
      const { error: profileError } = await supabase
        .from('seller_profiles')
        .upsert({
          user_id: currentUser.id,
          brand_name: brandName.trim(),
          website: website.trim() || null,
          business_category: businessCategory,
          business_description: businessDescription.trim() || null,
          contact_email: contactEmail.trim() || currentUser.email,
          contact_phone: contactPhone.trim() || null,
          gst_number: gstNumber.trim() || null,
          pan_number: panNumber.trim() || null,
          address: address.trim(),
          city: city.trim(),
          state: state,
          pincode: pincode,
          bank_name: bankName.trim(),
          account_number: accountNumber.trim(),
          ifsc_code: ifscCode.trim(),
          account_holder_name: accountHolderName.trim(),
          kyc_completed: !!(gstNumber && panNumber && address && city && state && pincode),
          onboarding_completed: true,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id' });

      if (profileError) throw profileError;

      // Always set role to 'seller' for business onboarding
      const { error: roleError } = await supabase
        .from('user_profiles')
        .update({ role: 'seller' })
        .eq('id', currentUser.id);

      if (roleError) {
        // Non-fatal: log but don't block success
        console.warn('Could not update user role:', roleError.message);
      }

      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="font-heading text-2xl font-bold text-foreground mb-2">You're all set!</h2>
          <p className="text-muted-foreground mb-8">
            Your brand profile has been created. Our team will review your details and activate your account within 24 hours. You can then start submitting products for review and testing.
          </p>
          <div className="flex flex-col gap-3">
            <Link
              href="/for-brands"
              className="cta-gradient text-primary-foreground font-semibold py-3 px-6 rounded-xl text-center font-heading"
            >
              Explore How It Works
            </Link>
            <Link href="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <AppLogo size={32} />
            <span className="font-heading font-bold text-foreground hidden sm:block">
              IndianReview<span className="text-primary">Club</span>
            </span>
          </Link>
          <span className="text-sm text-muted-foreground">Brand Onboarding</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-10">
        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-0 mb-10">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex items-center">
              <div className="flex flex-col items-center">
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                    step > s.id
                      ? 'bg-green-500 text-white'
                      : step === s.id
                      ? 'cta-gradient text-primary-foreground'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {step > s.id ? (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : s.id}
                </div>
                <span className={`text-xs mt-1.5 font-medium hidden sm:block ${step === s.id ? 'text-primary' : 'text-muted-foreground'}`}>
                  {s.label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`w-16 sm:w-24 h-0.5 mx-2 mb-4 transition-all ${step > s.id ? 'bg-green-400' : 'bg-border'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step 1 — Account */}
        {step === 1 && (
          <div className="max-w-md mx-auto">
            <div className="mb-8 text-center">
              <h1 className="font-heading text-2xl font-bold text-foreground mb-1">Create your brand account</h1>
              <p className="text-muted-foreground text-sm">Join 500+ brands getting authentic reviews on {SITE_NAME}</p>
            </div>

            {/* Auth mode toggle */}
            <div className="flex rounded-xl overflow-hidden border border-border mb-6">
              <button
                onClick={() => { setAuthMode('signup'); setFieldErrors({}); setError(''); }}
                className={`flex-1 py-2.5 text-sm font-medium transition-colors ${authMode === 'signup' ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:text-foreground'}`}
              >
                New Brand
              </button>
              <button
                onClick={() => { setAuthMode('signin'); setFieldErrors({}); setError(''); }}
                className={`flex-1 py-2.5 text-sm font-medium transition-colors ${authMode === 'signin' ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:text-foreground'}`}
              >
                Existing Account
              </button>
            </div>

            <form onSubmit={handleStep1} className="space-y-4" noValidate>
              {authMode === 'signup' && (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Full Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => { setFullName(e.target.value); clearFieldError('fullName'); }}
                    placeholder="Arjun Mehta"
                    className={`w-full px-4 py-2.5 rounded-xl border bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm ${fieldErrors.fullName ? 'border-red-400' : 'border-border'}`}
                  />
                  {fieldErrors.fullName && <p className="text-red-500 text-xs mt-1">{fieldErrors.fullName}</p>}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Business Email <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); clearFieldError('email'); }}
                  placeholder="you@brand.com"
                  className={`w-full px-4 py-2.5 rounded-xl border bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm ${fieldErrors.email ? 'border-red-400' : 'border-border'}`}
                />
                {fieldErrors.email && <p className="text-red-500 text-xs mt-1">{fieldErrors.email}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Password <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); clearFieldError('password'); }}
                    placeholder="Min. 8 characters"
                    className={`w-full px-4 py-2.5 pr-11 rounded-xl border bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm ${fieldErrors.password ? 'border-red-400' : 'border-border'}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-3 flex items-center text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )}
                  </button>
                </div>
                {fieldErrors.password && <p className="text-red-500 text-xs mt-1">{fieldErrors.password}</p>}
              </div>

              {error && error !== '__email_confirmation__' && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
                  {error}
                </div>
              )}

              {error === '__email_confirmation__' && (
                <div className="bg-green-50 border border-green-200 text-green-800 text-sm px-4 py-3 rounded-xl">
                  <p className="font-semibold mb-1">Check your email! 📧</p>
                  <p>We've sent a confirmation link to <strong>{email}</strong>. Click the link to verify your account, then come back and sign in using the "Existing Account" tab to continue.</p>
                </div>
              )}

              <button
                type="submit"
                disabled={saving}
                className="w-full cta-gradient text-primary-foreground font-semibold py-3 rounded-xl font-heading disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {saving ? (
                  <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Processing...</>
                ) : authMode === 'signup' ? 'Create Account & Continue' : 'Sign In & Continue'}
              </button>
            </form>
          </div>
        )}

        {/* Step 2 — Brand Profile */}
        {step === 2 && (
          <div>
            <div className="mb-8 text-center">
              <h1 className="font-heading text-2xl font-bold text-foreground mb-1">Set up your brand profile</h1>
              <p className="text-muted-foreground text-sm">This is what creators and reviewers will see when they browse your products</p>
            </div>

            <form onSubmit={handleStep2} className="space-y-5" noValidate>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-1.5">Brand Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={brandName}
                    onChange={(e) => { setBrandName(e.target.value); clearFieldError('brandName'); }}
                    placeholder="e.g. Noise, boAt, Mamaearth"
                    className={`w-full px-4 py-2.5 rounded-xl border bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm ${fieldErrors.brandName ? 'border-red-400' : 'border-border'}`}
                  />
                  {fieldErrors.brandName && <p className="text-red-500 text-xs mt-1">{fieldErrors.brandName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Website</label>
                  <input
                    type="url"
                    value={website}
                    onChange={(e) => { setWebsite(e.target.value); clearFieldError('website'); }}
                    placeholder="https://yourbrand.com"
                    className={`w-full px-4 py-2.5 rounded-xl border bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm ${fieldErrors.website ? 'border-red-400' : 'border-border'}`}
                  />
                  {fieldErrors.website && <p className="text-red-500 text-xs mt-1">{fieldErrors.website}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Business Category <span className="text-red-500">*</span></label>
                  <select
                    value={businessCategory}
                    onChange={(e) => { setBusinessCategory(e.target.value); clearFieldError('businessCategory'); }}
                    className={`w-full px-4 py-2.5 rounded-xl border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm ${fieldErrors.businessCategory ? 'border-red-400' : 'border-border'}`}
                  >
                    {BUSINESS_CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  {fieldErrors.businessCategory && <p className="text-red-500 text-xs mt-1">{fieldErrors.businessCategory}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Contact Email</label>
                  <input
                    type="email"
                    value={contactEmail}
                    onChange={(e) => { setContactEmail(e.target.value); clearFieldError('contactEmail'); }}
                    placeholder="contact@brand.com"
                    className={`w-full px-4 py-2.5 rounded-xl border bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm ${fieldErrors.contactEmail ? 'border-red-400' : 'border-border'}`}
                  />
                  {fieldErrors.contactEmail && <p className="text-red-500 text-xs mt-1">{fieldErrors.contactEmail}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Contact Phone</label>
                  <input
                    type="tel"
                    value={contactPhone}
                    onChange={(e) => { setContactPhone(e.target.value); clearFieldError('contactPhone'); }}
                    placeholder="+91 98765 43210"
                    className={`w-full px-4 py-2.5 rounded-xl border bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm ${fieldErrors.contactPhone ? 'border-red-400' : 'border-border'}`}
                  />
                  {fieldErrors.contactPhone && <p className="text-red-500 text-xs mt-1">{fieldErrors.contactPhone}</p>}
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-1.5">Business Description</label>
                  <textarea
                    value={businessDescription}
                    onChange={(e) => setBusinessDescription(e.target.value)}
                    placeholder="Tell reviewers what your brand is about, the products you'd like reviewed, and what makes you unique..."
                    rows={3}
                    className="w-full px-4 py-2.5 rounded-xl border border-border bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm resize-none"
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
                  {error}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                {!user && (
                  <button
                    type="button"
                    onClick={() => { setStep(1); setFieldErrors({}); }}
                    className="flex-1 py-3 rounded-xl border border-border text-foreground font-medium text-sm hover:bg-muted transition-colors"
                  >
                    Back
                  </button>
                )}
                <button
                  type="submit"
                  className="flex-1 cta-gradient text-primary-foreground font-semibold py-3 rounded-xl font-heading"
                >
                  Continue to Verification
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Step 3 — KYC */}
        {step === 3 && (
          <div>
            <div className="mb-8 text-center">
              <h1 className="font-heading text-2xl font-bold text-foreground mb-1">Business verification</h1>
              <p className="text-muted-foreground text-sm">Required to activate your brand account and start submitting products for review. All data is encrypted.</p>
            </div>

            <form onSubmit={handleStep3} className="space-y-8" noValidate>
              {/* Business Identity */}
              <div>
                <h3 className="font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-bold">1</span>
                  Business Identity
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">GST Number <span className="text-muted-foreground text-xs font-normal">(optional)</span></label>
                    <input
                      type="text"
                      value={gstNumber}
                      onChange={(e) => { setGstNumber(e.target.value.toUpperCase()); clearFieldError('gstNumber'); }}
                      placeholder="22AAAAA0000A1Z5"
                      maxLength={15}
                      className={`w-full px-4 py-2.5 rounded-xl border bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm font-mono ${fieldErrors.gstNumber ? 'border-red-400' : 'border-border'}`}
                    />
                    {fieldErrors.gstNumber && <p className="text-red-500 text-xs mt-1">{fieldErrors.gstNumber}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">PAN Number <span className="text-muted-foreground text-xs font-normal">(optional)</span></label>
                    <input
                      type="text"
                      value={panNumber}
                      onChange={(e) => { setPanNumber(e.target.value.toUpperCase()); clearFieldError('panNumber'); }}
                      placeholder="ABCDE1234F"
                      maxLength={10}
                      className={`w-full px-4 py-2.5 rounded-xl border bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm font-mono ${fieldErrors.panNumber ? 'border-red-400' : 'border-border'}`}
                    />
                    {fieldErrors.panNumber && <p className="text-red-500 text-xs mt-1">{fieldErrors.panNumber}</p>}
                  </div>
                </div>
              </div>

              {/* Business Address */}
              <div>
                <h3 className="font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-bold">2</span>
                  Registered Business Address
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-foreground mb-1.5">Street Address <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={address}
                      onChange={(e) => { setAddress(e.target.value); clearFieldError('address'); }}
                      placeholder="Building, Street, Area"
                      className={`w-full px-4 py-2.5 rounded-xl border bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm ${fieldErrors.address ? 'border-red-400' : 'border-border'}`}
                    />
                    {fieldErrors.address && <p className="text-red-500 text-xs mt-1">{fieldErrors.address}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">City <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={city}
                      onChange={(e) => { setCity(e.target.value); clearFieldError('city'); }}
                      placeholder="Mumbai"
                      className={`w-full px-4 py-2.5 rounded-xl border bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm ${fieldErrors.city ? 'border-red-400' : 'border-border'}`}
                    />
                    {fieldErrors.city && <p className="text-red-500 text-xs mt-1">{fieldErrors.city}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">State <span className="text-red-500">*</span></label>
                    <select
                      value={state}
                      onChange={(e) => { setState(e.target.value); clearFieldError('state'); }}
                      className={`w-full px-4 py-2.5 rounded-xl border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm ${fieldErrors.state ? 'border-red-400' : 'border-border'}`}
                    >
                      <option value="">Select state</option>
                      {INDIAN_STATES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    {fieldErrors.state && <p className="text-red-500 text-xs mt-1">{fieldErrors.state}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Pincode <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={pincode}
                      onChange={(e) => { setPincode(e.target.value.replace(/\D/g, '').slice(0, 6)); clearFieldError('pincode'); }}
                      placeholder="400001"
                      maxLength={6}
                      className={`w-full px-4 py-2.5 rounded-xl border bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm ${fieldErrors.pincode ? 'border-red-400' : 'border-border'}`}
                    />
                    {fieldErrors.pincode && <p className="text-red-500 text-xs mt-1">{fieldErrors.pincode}</p>}
                  </div>
                </div>
              </div>

              {/* Bank Details */}
              <div>
                <h3 className="font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-bold">3</span>
                  Bank Account Details
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Account Holder Name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={accountHolderName}
                      onChange={(e) => { setAccountHolderName(e.target.value); clearFieldError('accountHolderName'); }}
                      placeholder="As per bank records"
                      className={`w-full px-4 py-2.5 rounded-xl border bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm ${fieldErrors.accountHolderName ? 'border-red-400' : 'border-border'}`}
                    />
                    {fieldErrors.accountHolderName && <p className="text-red-500 text-xs mt-1">{fieldErrors.accountHolderName}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Bank Name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={bankName}
                      onChange={(e) => { setBankName(e.target.value); clearFieldError('bankName'); }}
                      placeholder="HDFC Bank"
                      className={`w-full px-4 py-2.5 rounded-xl border bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm ${fieldErrors.bankName ? 'border-red-400' : 'border-border'}`}
                    />
                    {fieldErrors.bankName && <p className="text-red-500 text-xs mt-1">{fieldErrors.bankName}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Account Number <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={accountNumber}
                      onChange={(e) => { setAccountNumber(e.target.value.replace(/\D/g, '')); clearFieldError('accountNumber'); }}
                      placeholder="Account number"
                      className={`w-full px-4 py-2.5 rounded-xl border bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm ${fieldErrors.accountNumber ? 'border-red-400' : 'border-border'}`}
                    />
                    {fieldErrors.accountNumber && <p className="text-red-500 text-xs mt-1">{fieldErrors.accountNumber}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">IFSC Code <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={ifscCode}
                      onChange={(e) => { setIfscCode(e.target.value.toUpperCase()); clearFieldError('ifscCode'); }}
                      placeholder="HDFC0001234"
                      maxLength={11}
                      className={`w-full px-4 py-2.5 rounded-xl border bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm font-mono ${fieldErrors.ifscCode ? 'border-red-400' : 'border-border'}`}
                    />
                    {fieldErrors.ifscCode && <p className="text-red-500 text-xs mt-1">{fieldErrors.ifscCode}</p>}
                  </div>
                </div>
              </div>

              {/* Privacy note */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex gap-3">
                <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <p className="text-xs text-amber-800">
                  Your KYC data is encrypted and stored securely. It is used only for identity verification and payout processing. We never share it with third parties.
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
                  {error}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setStep(2); setFieldErrors({}); }}
                  className="flex-1 py-3 rounded-xl border border-border text-foreground font-medium text-sm hover:bg-muted transition-colors"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 cta-gradient text-primary-foreground font-semibold py-3 rounded-xl font-heading disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Saving...</>
                  ) : 'Complete Onboarding'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
